import {ArchiveItem} from "../types";

type IsoWeek = {
    weekYear: number;
    weekNumber: number;
};

export type ArchiveGroup = {
    years: Map<number, Map<string, Map<string, Map<string, ArchiveItem[]>>>>;
};

const pad2 = (n: number) => String(n).padStart(2, "0");

const toDateKey = (d: Date) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;

const toMonthKey = (d: Date) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;

const parseIndexKeyToUtcDate = (id: string): Date | null => {
    const s = (id || "").trim();
    if (s.length < 8) {
        return null;
    }
    const head = s.slice(0, 8);
    if (!/^\d{8}$/.test(head)) {
        return null;
    }
    const year = Number(head.slice(0, 4));
    const month = Number(head.slice(4, 6));
    const day = Number(head.slice(6, 8));
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
        return null;
    }
    if (year < 1970 || month < 1 || month > 12 || day < 1 || day > 31) {
        return null;
    }
    const d = new Date(Date.UTC(year, month - 1, day));
    if (Number.isNaN(d.getTime())) {
        return null;
    }
    return d;
};

const getIsoWeek = (date: Date): IsoWeek => {
    const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const weekYear = d.getUTCFullYear();
    const yearStart = new Date(Date.UTC(weekYear, 0, 1));
    const diffDays = Math.floor((d.getTime() - yearStart.getTime()) / 86400000);
    const weekNumber = Math.ceil((diffDays + 1) / 7);
    return {weekYear, weekNumber};
};

export const groupArchive = (records: ArchiveItem[]): ArchiveGroup => {
    const years = new Map<number, Map<string, Map<string, Map<string, ArchiveItem[]>>>>();
    for (const r of records) {
        const d = parseIndexKeyToUtcDate(r.id);
        if (!d) {
            continue;
        }
        const year = d.getUTCFullYear();
        const monthKey = toMonthKey(d);
        const iso = getIsoWeek(d);
        const weekKey = `${iso.weekYear}-W${pad2(iso.weekNumber)}`;
        const dayKey = toDateKey(d);
        if (!years.has(year)) {
            years.set(year, new Map());
        }
        const months = years.get(year)!;
        if (!months.has(monthKey)) {
            months.set(monthKey, new Map());
        }
        const weeks = months.get(monthKey)!;
        if (!weeks.has(weekKey)) {
            weeks.set(weekKey, new Map());
        }
        const days = weeks.get(weekKey)!;
        if (!days.has(dayKey)) {
            days.set(dayKey, []);
        }
        days.get(dayKey)!.push(r);
    }
    years.forEach((months) => {
        months.forEach((weeks) => {
            weeks.forEach((dayMap) => {
                dayMap.forEach((arr) => {
                    arr.sort((a, b) => (a.id < b.id ? 1 : -1));
                });
            });
        });
    });
    return {years};
};
